<template>
  <FreeBottomSheet :name="name" @onClose="onClose">
    <template #header>
      <FreeHeader class="h-16 p-4" style="--free-header-action-width: auto">
        <template v-if="!filterStage.isMainStage.value" #leftAction>
          <FreeBackButton
            class="back-button-color mr-4"
            @click="
              filterStage.setFilterStage(filtersConfig.FILTER_STAGES.MAIN)
            "
          />
        </template>
        <template #default>
          <h1 class="text-text-neutral-primary font-medium">
            {{ filterStage.stageTitle.value }}
          </h1>
        </template>
        <template #rightAction>
          <FreeButton
            type="button"
            class="gray-button w-20 px-4 py-1 text-xs font-medium"
            :disabled="!isFiltered[filterStage.filterStage.value]"
            @click="handleFilterReset"
          >
            {{ $t('general.clear') }}
          </FreeButton>
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div class="relative flex min-h-[75vh] flex-col space-y-8 px-4">
        <div class="flex-grow">
          <Transition name="filters-stage">
            <FiltersMain
              v-if="filterStage.isMainStage.value"
              :offers="offers"
              :airlines="airlines"
              :limits="filterLimits"
              :state="state"
              :routes="routes"
              @onStageChange="filterStage.setFilterStage($event)"
              @onPriceUpdate="emit('onPriceChange', $event)"
              @onNightTransferFilterChange="
                emit('onNightTransferFilterChange', $event)
              "
              @onTransferFilterChange="emit('onTransferFilterChange', $event)"
              @onFlightDurationUpdate="emit('onFlightDurationUpdate', $event)"
              @onFlightFilterChange="onFlightFilterChange"
            />
            <TimeFilters
              v-else-if="
                filterStage.isActiveStage(filtersConfig.FILTER_STAGES.TIME)
              "
              :routes="routes"
              :state="state.flights"
              @onFlightFilterChange="onFlightFilterChange"
            />
            <TransferFilters
              v-else-if="
                filterStage.isActiveStage(filtersConfig.FILTER_STAGES.TRANSFERS)
              "
              :state="state"
              :offers="offers"
              :limits="filterLimits.transferDuration"
              @onTransferFilterChange="emit('onTransferFilterChange', $event)"
              @onNightTransferFilterChange="
                emit('onNightTransferFilterChange', $event)
              "
            />
            <AirlinesFilters
              v-else
              :state="state"
              :airlines="airlines"
              @onAirlinesFilterChange="emit('onAirlinesFilterChange', $event)"
            />
          </Transition>
        </div>
        <div
          class="bg-background-neutral-secondary sticky bottom-4 z-10 w-full rounded-2xl"
        >
          <FreeButton
            type="button"
            class="green-main-button z-10 w-full"
            :disabled="!hasFilteredOffers"
            @click="handleFiltersApply"
          >
            {{ buttonText }}
          </FreeButton>
        </div>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import type {
    FilterState,
    FlightFilterState,
  } from '@aviata/offers-filters-fns';
  import type { Dict } from '@aviata/platform-types';
  import {
    FreeBottomSheet,
    FreeHeader,
    FreeButton,
    FreeBackButton,
    flightEntity,
  } from 'freedom-shared';
  import { airlineFilters } from 'shared';

  import {
    searchOffersLib,
    searchOffersTypes,
  } from '@/features/search-offers-filter';
  import { TimeFilters } from '@/features/time-filter';
  import { TransferFilters } from '@/features/transfer-filters';

  import { sentryModel } from '@/shared/model';
  import { analyticsEventsLib } from '@/shared/lib';

  import type { useFilterStage } from '../model';
  import { filtersConfig } from '../config';
  import AirlinesFilters from './AirlinesFilters.vue';
  import FiltersMain from './FiltersMain.vue';

  const props = defineProps<{
    name: string;
    state: FilterState;
    filterStage: ReturnType<typeof useFilterStage>;
    filterLimits: searchOffersTypes.FilterLimits;
    offers: flightEntity.types.MappedOffer[];
    airlines: Dict.Entity['airlines'];
    routes: searchOffersTypes.MappedFlightPoint[];
    filteredOffers: flightEntity.types.MappedOffer[];
  }>();

  const emit = defineEmits<{
    onPriceChange: [prices: number[]];
    onFlightsFilterChange: [
      flightsFilterState: Partial<FlightFilterState>,
      flightNumber: number,
    ];
    onTransferFilterChange: [flightsFilterState: Partial<FlightFilterState>];
    onAirlinesFilterChange: [payload: airlineFilters.types.AirlineFiltersState];
    onNightTransferFilterChange: [isWithoutNightTransfer: boolean];
    onFlightDurationUpdate: [flightDuration: number[]];
    onClearAirlinesFilter: [];
    onClearTimeFilter: [];
    onClearTransferFilter: [];
    onClearAllFilter: [];
    onCommit: [];
    onClose: [];
  }>();

  const { t } = useI18n();

  const hasFilteredOffers = computed(() => props.filteredOffers.length > 0);
  const buttonText = computed(() => {
    if (!hasFilteredOffers.value) {
      return t('search.filters.noResultsWithFilter');
    }

    if (props.filteredOffers.length === props.offers.length) {
      return t('search.filters.showAllOffersCount', {
        count: props.offers.length,
      });
    }

    return t('search.filters.showFilteredOffers', {
      n: props.filteredOffers.length,
      count: props.offers.length,
    });
  });

  const isFiltered = computed(() => ({
    [filtersConfig.FILTER_STAGES.MAIN]: searchOffersLib.checkAppliedFilters({
      state: props.state,
      limits: props.filterLimits,
    }).isAnyFilterApplied,
    [filtersConfig.FILTER_STAGES.AIRLINES]:
      searchOffersLib.isAirlinesFilterApplied({
        state: props.state,
      }),
    [filtersConfig.FILTER_STAGES.TIME]:
      searchOffersLib.isFlightTimeFiltersApplied({
        state: props.state,
      }),
    [filtersConfig.FILTER_STAGES.TRANSFERS]:
      searchOffersLib.isTransferFiltersApplied({
        state: props.state,
        limits: props.filterLimits,
      }),
  }));

  const onFlightFilterChange = (
    flightFilterState: Partial<FlightFilterState>,
    flightNumber: number,
  ) => emit('onFlightsFilterChange', flightFilterState, flightNumber);

  const onClose = () => {
    emit('onClose');
    props.filterStage.setFilterStage(filtersConfig.FILTER_STAGES.MAIN);
  };

  const stageToResetSource: Record<string, string> = {
    [filtersConfig.FILTER_STAGES.MAIN]: 'main_filters',
    [filtersConfig.FILTER_STAGES.AIRLINES]: 'airlines',
    [filtersConfig.FILTER_STAGES.TIME]: 'time',
    [filtersConfig.FILTER_STAGES.TRANSFERS]: 'transfers',
  };

  const handleFilterReset = () => {
    const source =
      stageToResetSource[props.filterStage.filterStage.value] ?? 'main_filters';
    analyticsEventsLib.resetFiltersClick(source);

    switch (props.filterStage.filterStage.value) {
      case filtersConfig.FILTER_STAGES.AIRLINES:
        emit('onClearAirlinesFilter');
        break;
      case filtersConfig.FILTER_STAGES.TIME:
        emit('onClearTimeFilter');
        break;
      case filtersConfig.FILTER_STAGES.TRANSFERS:
        emit('onClearTransferFilter');
        break;
      default:
        emit('onClearAllFilter');
    }

    sentryModel.addBreadcrumb({
      category: sentryModel.breadcrumbCategories.SEARCH,
      message: 'Clear selected filters',
      level: sentryModel.level.INFO,
    });
  };

  const handleFiltersApply = () => {
    analyticsEventsLib.applyFiltersClick();
    emit('onCommit');
  };
</script>

<style scoped>
  .back-button-color {
    --return-button-color: theme('colors.gray.500');
  }

  .filters-stage-enter-active {
    transition: all 0.8s ease;
  }
  .filters-stage-leave-active {
    transition: all 0.2s ease;
  }
  .filters-stage-enter-from,
  .filters-stage-leave-to {
    opacity: 0;
    position: absolute;
    transform: translateX(-30px);
  }
</style>
